import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap, map } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';

export interface LoginCredentials {
  email: string;
  password: string;
}

// Represents the user data and token within the API response's 'data' field
export interface UserData {
  _id: string;
  first_name: string;
  last_name: string;
  email: string;
  token: string;
}

// Represents the full API response structure for login
export interface ApiLoginResponse {
  code: number;
  data: UserData;
  message: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  // Subject now holds UserData or null
  private currentUserSubject = new BehaviorSubject<UserData | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router
  ) {
    this.loadStoredUser();
  }

  private loadStoredUser(): void {
    const storedUser = localStorage.getItem('userData');
    if (storedUser) {
      this.currentUserSubject.next(JSON.parse(storedUser));
    }
  }

  login(credentials: LoginCredentials): Observable<UserData> {
    return this.http.post<ApiLoginResponse>(`${environment.API_BASE_URL}/login`, credentials).pipe(
      map(response => response.data),
      tap(userData => {
        localStorage.setItem('userData', JSON.stringify(userData));
        this.currentUserSubject.next(userData);
      })
    );
  }

  logout(): void {
    localStorage.removeItem('userData');
    this.currentUserSubject.next(null);
    this.router.navigate(['/login']);
  }

  getStoredToken(): string | null {
    const userData = this.currentUserSubject.value;
    return userData ? userData.token : null;
  }

  isAuthenticated(): boolean {
    return !!this.getStoredToken();
  }
}
